import { useRef, useState } from "react";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from "framer-motion";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Mark } from "@/components/Mark";

const scrollToProcess = () => {
  const target = document.querySelector("#proceso");
  if (window.__lenis && target) {
    window.__lenis.scrollTo(target, { duration: 1.6 });
  } else {
    target?.scrollIntoView({ behavior: "smooth" });
  }
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const seg = (p, a, b) => clamp01((p - a) / (b - a));
const fade = (p, a, b, c, d) => seg(p, a, b) * (1 - seg(p, c, d));

const QUOTE_WORDS = [
  { t: "Entender", key: 0 },
  { t: "cada" },
  { t: "negocio", key: 1 },
  { t: "y" },
  { t: "transformar" },
  { t: "sus" },
  { t: "productos,", key: 2 },
  { t: "servicios", key: 3 },
  { t: "y" },
  { t: "día", key: 4 },
  { t: "a", key: 4 },
  { t: "día", key: 4 },
  { t: "en" },
  { t: "contenido", key: 5 },
  { t: "interesante." },
];

function SceneLayer({ opacity, y = 0, children, testId }) {
  if (opacity <= 0.001) return null;
  return (
    <div
      data-testid={testId}
      className="absolute inset-0 flex items-center px-6 md:px-12 lg:px-20"
      style={{ opacity, transform: `translateY(${y}px)`, pointerEvents: "none" }}
    >
      {children}
    </div>
  );
}

function IntroScene({ p }) {
  const opacity = 1 - seg(p, 0.05, 0.08);
  const y = -60 * seg(p, 0.05, 0.09);
  return (
    <SceneLayer opacity={opacity} y={y} testId="hero-intro">
      <div className="w-full max-w-5xl">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.7] }}
          transition={{ duration: 2.6, times: [0, 0.35, 1], delay: 0.3 }}
          className="text-xs uppercase tracking-[0.3em] text-mirian-wine"
        >
          Una candidatura que empezó por una frase
        </motion.p>
        <h1 className="mt-10 font-serif text-4xl uppercase leading-[1.12] sm:text-5xl lg:text-7xl">
          <MaskedLine delay={1.2}>Hay una frase</MaskedLine>
          <MaskedLine delay={1.45}>en vuestra oferta</MaskedLine>
          <MaskedLine delay={1.7}>que me hizo…</MaskedLine>
        </h1>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 0.2 }}
          aria-hidden="true"
          className="caret-blink mt-6 block font-serif text-4xl text-mirian-wine sm:text-5xl lg:text-6xl"
        >
          |
        </motion.span>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 inline-block text-xs uppercase tracking-[0.24em] text-mirian-wine/70"
        >
          <span className="border-b-2 border-mirian-lime pb-1">
            ¿seguir leyendo?
          </span>
        </motion.p>
      </div>
    </SceneLayer>
  );
}

function QuoteScene({ p }) {
  const opacity = fade(p, 0.6, 0.645, 0.83, 0.87);
  const y = 36 * (1 - seg(p, 0.6, 0.645)) - 56 * seg(p, 0.83, 0.87);
  if (opacity <= 0.001) return null;
  const entOn = p > 0.775;
  return (
    <SceneLayer opacity={opacity} y={y} testId="hero-quote-scene">
      <div className="w-full max-w-4xl">
        <p
          data-testid="offer-quote"
          className="font-serif text-2xl leading-snug sm:text-3xl lg:text-[2.6rem] lg:leading-[1.25]"
        >
          «
          {QUOTE_WORDS.map((w, i) => {
            if (w.key === undefined) {
              return <span key={i} className="opacity-35">{w.t} </span>;
            }
            const wk = 0.655 + w.key * 0.02;
            let o = 0.35 + 0.65 * seg(p, wk - 0.015, wk);
            if (w.key !== 0) o = o * (1 - seg(p, 0.77, 0.778)) + 0.35 * seg(p, 0.77, 0.778);
            return (
              <span key={i}>
                <span className="inline-block" style={{ opacity: o }}>
                  {w.key === 0 ? (
                    <span className={`mark-lime${entOn ? " mark-on" : ""}`}>{w.t}</span>
                  ) : (
                    w.t
                  )}
                </span>{" "}
              </span>
            );
          })}
          »
        </p>
        <p
          style={{ opacity: seg(p, 0.775, 0.8) }}
          className="mt-6 text-xs uppercase tracking-[0.22em] text-mirian-wine/50"
        >
          De vuestra oferta — Especialista en Redes Sociales y Creación de
          Contenido · 20 h / semana
        </p>
      </div>
    </SceneLayer>
  );
}

function ClosingScene({ p }) {
  const o1 = seg(p, 0.855, 0.885);
  const o2 = seg(p, 0.885, 0.915);
  const underline = seg(p, 0.92, 0.955);
  const o3 = seg(p, 0.945, 0.97);
  if (o1 <= 0.001) return null;
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center px-6 md:px-12 lg:px-20">
      <div className="w-full max-w-5xl">
        <p style={{ opacity: o1 }} className="text-base text-mirian-wine/70 md:text-lg">
          Y pensé:
        </p>
        <p
          style={{ opacity: o2, transform: `translateY(${30 * (1 - o2)}px)` }}
          className="mt-8 font-serif text-3xl leading-[1.15] sm:text-4xl lg:text-6xl"
        >
          antes de contaros quién soy, quizá tenga más sentido enseñaros{" "}
          <span className="relative inline-block italic">
            cómo pienso
            <svg
              viewBox="0 0 260 14"
              className="absolute -bottom-2 left-0 w-full"
              aria-hidden="true"
            >
              <motion.path
                d="M4 10 C 70 2, 180 2, 256 8"
                fill="none"
                stroke="#C7F000"
                strokeWidth="4"
                strokeLinecap="round"
                style={{ pathLength: underline }}
              />
            </svg>
          </span>
          .
        </p>
        <div
          style={{ opacity: o3, pointerEvents: p > 0.94 ? "auto" : "none" }}
          className="mt-16"
        >
          <button
            data-testid="hero-scroll-cta"
            onClick={scrollToProcess}
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-mirian-wine"
          >
            <span className="border-b border-transparent pb-0.5 transition-colors duration-300 group-hover:border-mirian-lime">
              El proceso, paso a paso
            </span>
            <span aria-hidden="true">↓</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function StaticHero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-x-clip px-6 pb-32 pt-40 md:px-12 lg:px-20"
    >
      <div className="max-w-5xl space-y-14">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-mirian-wine/70">
            Una candidatura que empezó por una frase
          </p>
        </Reveal>
        <Reveal>
          <h1 className="font-serif text-4xl uppercase leading-[1.12] sm:text-5xl lg:text-7xl">
            Hay una frase en vuestra oferta que me hizo dejar de leer.
          </h1>
        </Reveal>
        <Reveal>
          <p className="text-base text-mirian-wine/70 md:text-lg">
            No porque estuviera mal.
          </p>
        </Reveal>
        <Reveal>
          <p className="font-serif text-2xl italic sm:text-3xl">Al contrario.</p>
        </Reveal>
        <Reveal>
          <p className="max-w-3xl font-serif text-2xl leading-snug sm:text-3xl lg:text-4xl">
            Porque describe precisamente{" "}
            <span className="italic">
              la parte del marketing que más me interesa.
            </span>
          </p>
        </Reveal>
        <Reveal>
          <blockquote
            data-testid="offer-quote"
            className="max-w-3xl border-l-2 border-mirian-wine pl-6 md:pl-10"
          >
            <p className="font-serif text-xl leading-snug sm:text-2xl lg:text-[2rem]">
              «<Mark>Entender</Mark> cada negocio y transformar sus productos,
              servicios y día a día en contenido interesante.»
            </p>
            <cite className="mt-5 block text-xs not-italic uppercase tracking-[0.22em] text-mirian-wine/50">
              De vuestra oferta — Especialista en Redes Sociales y Creación de
              Contenido · 20 h / semana
            </cite>
          </blockquote>
        </Reveal>
        <Reveal>
          <p className="text-base text-mirian-wine/70 md:text-lg">Y pensé:</p>
        </Reveal>
        <Reveal>
          <p className="font-serif text-3xl leading-[1.15] sm:text-4xl lg:text-6xl">
            antes de contaros quién soy, quizá tenga más sentido enseñaros{" "}
            <span className="italic">cómo pienso</span>.
          </p>
        </Reveal>
        <Reveal>
          <button
            data-testid="hero-scroll-cta"
            onClick={scrollToProcess}
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-mirian-wine"
          >
            <span className="border-b border-transparent pb-0.5 transition-colors duration-300 group-hover:border-mirian-lime">
              El proceso, paso a paso
            </span>
            <span aria-hidden="true">↓</span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [p, setP] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => setP(v));

  if (reduce) return <StaticHero />;

  const bigOpacity = fade(p, 0.05, 0.1, 0.19, 0.26);
  const bigScale = 0.72 + 0.28 * seg(p, 0.05, 0.17);
  const bigY = -90 * seg(p, 0.19, 0.26);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative"
      style={{ height: "620vh" }}
    >
      <div className="sticky top-0 h-screen overflow-x-clip">
        <IntroScene p={p} />

        <SceneLayer
          opacity={bigOpacity}
          y={bigY}
          testId="hero-big-reveal"
        >
          <p
            className="font-serif uppercase leading-[0.95] text-mirian-wine text-[clamp(3.4rem,13vw,11rem)]"
            style={{ transform: `scale(${bigScale})`, transformOrigin: "left center" }}
          >
            Dejar
            <br />
            de leer.
          </p>
        </SceneLayer>

        <SceneLayer opacity={fade(p, 0.26, 0.3, 0.345, 0.38)} y={36 * (1 - seg(p, 0.26, 0.3)) - 56 * seg(p, 0.345, 0.38)}>
          <p className="max-w-md text-base text-mirian-wine/70 md:text-lg">
            No porque estuviera mal.
          </p>
        </SceneLayer>

        <SceneLayer opacity={fade(p, 0.365, 0.395, 0.44, 0.47)} y={36 * (1 - seg(p, 0.365, 0.395)) - 56 * seg(p, 0.44, 0.47)}>
          <p className="font-serif text-3xl italic sm:text-4xl lg:text-5xl">
            Al contrario.
          </p>
        </SceneLayer>

        <SceneLayer opacity={fade(p, 0.465, 0.5, 0.565, 0.605)} y={36 * (1 - seg(p, 0.465, 0.5)) - 56 * seg(p, 0.565, 0.605)}>
          <p className="max-w-3xl font-serif text-2xl leading-snug sm:text-3xl lg:text-5xl">
            Porque describe precisamente{" "}
            <span className="italic">
              la parte del marketing que más me interesa.
            </span>
          </p>
        </SceneLayer>

        <QuoteScene p={p} />
        <ClosingScene p={p} />
      </div>
    </section>
  );
}
